import type { MapUpdate } from './intents';
import type { PropertySearchStore } from './store';
import type { PropertyFilters } from '~/core/entities/property/filters';

import { slugify } from '~~/utils/slugify';
import { watch } from 'vue';

export type LocationChangeType =
	| 'addition'
	| 'removal'
	| 'substitution'
	| 'none';

export function getLocationChangeType(
	newFilters: PropertyFilters,
	currentFilters: PropertyFilters
): LocationChangeType {
	const getArrayLength = (arr: readonly unknown[] | undefined) =>
		arr?.length || 0;

	const current = {
		regions: getArrayLength(currentFilters.regions),
		cities: getArrayLength(currentFilters.cities),
		macroregions: getArrayLength(currentFilters.macroregions),
		hasAddress: !!currentFilters.addressSlug,
		hasPoi:
			Number.isFinite(currentFilters.poiLocationLat) &&
			Number.isFinite(currentFilters.poiLocationLng)
	};

	const updated = {
		regions: getArrayLength(newFilters.regions),
		cities: getArrayLength(newFilters.cities),
		macroregions: getArrayLength(newFilters.macroregions),
		hasAddress: !!newFilters.addressSlug,
		hasPoi:
			Number.isFinite(newFilters.poiLocationLat) &&
			Number.isFinite(newFilters.poiLocationLng)
	};

	const hasLocationSubstitution =
		(newFilters.regions?.[0] !== currentFilters.regions?.[0] &&
			!!newFilters.regions?.[0]) ||
		(newFilters.cities?.[0] !== currentFilters.cities?.[0] &&
			!!newFilters.cities?.[0]) ||
		(newFilters.states?.[0] !== currentFilters.states?.[0] &&
			!!newFilters.states?.[0]) ||
		(newFilters.macroregions?.[0] !== currentFilters.macroregions?.[0] &&
			!!newFilters.macroregions?.[0]) ||
		(!!newFilters.locationSlug &&
			!!currentFilters.locationSlug &&
			newFilters.locationSlug !== currentFilters.locationSlug) ||
		(newFilters.addressSlug !== currentFilters.addressSlug &&
			updated.hasAddress) ||
		((newFilters.poiLocationLat !== currentFilters.poiLocationLat ||
			newFilters.poiLocationLng !== currentFilters.poiLocationLng) &&
			updated.hasPoi);

	if (hasLocationSubstitution) return 'substitution';

	const hasLocationAddition =
		updated.regions > current.regions ||
		updated.cities > current.cities ||
		updated.macroregions > current.macroregions ||
		(!current.hasAddress && updated.hasAddress) ||
		(!current.hasPoi && updated.hasPoi);

	if (hasLocationAddition) return 'addition';

	const hasLocationRemoval =
		updated.regions < current.regions ||
		updated.cities < current.cities ||
		updated.macroregions < current.macroregions ||
		(current.hasAddress && !updated.hasAddress) ||
		(current.hasPoi && !updated.hasPoi);

	if (hasLocationRemoval) return 'removal';

	return 'none';
}

// The listing API enriches slugs with names that later appear in the query.
// Those redundant names must not turn a price/type change into a camera move.
export function getMapRouteLocationChangeType(
	next: PropertyFilters,
	previous: PropertyFilters
): LocationChangeType {
	const locationFromRoute = (value: PropertyFilters): PropertyFilters => ({
		...value,
		cities: value.cities?.filter(
			city => !value.locationSlug?.includes(slugify(city))
		),
		states: value.states?.filter(
			state => !value.locationSlug?.includes(slugify(state))
		),
		regions: value.regions?.filter(
			// Only the first neighborhood is encoded in the path.
			(region, index) =>
				index > 0 || !value.locationSlug?.startsWith(`${slugify(region)}-`)
		)
	});
	return getLocationChangeType(
		locationFromRoute(next),
		locationFromRoute(previous)
	);
}

// Navigation without a gesture (history, shared link): only a location change reframes.
export function mapUpdateForHistory(change: LocationChangeType): MapUpdate {
	return change === 'none' ? 'keep' : 'refit';
}

export type MapUpdateEffects = Record<
	MapUpdate,
	(next: PropertyFilters) => void
>;

// Compares URL states only: the API enrichment (cities/states) never reaches the URL,
// so adopting it would turn any later filter change into a location "removal".
export function watchMapUpdates(
	search: Pick<PropertySearchStore, 'revision' | 'confirmedFilters'>,
	takeGestureUpdate: () => MapUpdate | undefined,
	effects: MapUpdateEffects
) {
	let previous = search.confirmedFilters.value;
	return watch(
		search.revision,
		() => {
			const next = search.confirmedFilters.value;
			const change = getLocationChangeType(next, previous);
			previous = next;
			effects[takeGestureUpdate() ?? mapUpdateForHistory(change)](next);
		},
		{ flush: 'post' }
	);
}
